import { AssistantCustomer } from '@mews-commander/data-access';
import { LocalizedText, useLocalizeText } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { Icon, IconName, Stack, Tag } from '@optimus-web/core';
import { useResolveRoute } from '@mews/routing';
import { UnicodeEntity } from '@mews/enums';
import { getFullName, useCommanderSelector } from '@mews-commander/utils';
import { getCurrentEnterpriseChainId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentProfileEnabledToManageCustomers } from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { navigateInBaseModal, showRouteInModal } from '../../../app/actions/common';
import { highlight } from '../../utils/highlight';
import { ResultListProps } from '../results/ListTitle';
import { StyledBadge, StyledLeftIcon } from '../styles';
import { AssistantSearchType, SearchType } from '../../assistantSearchTypes';
import { AssistantSearchListItemButton } from '../results/searchListItemButton';
import { AssistantFilterChips } from '../../assistantSearchTypeFilters';
import { DescriptionItem, SearchItemDescription } from '../components/SearchItemDescription';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface CustomerListProps extends ResultListProps {
    customers: AssistantCustomer[];
    selectedFilter?: AssistantSearchType | AssistantFilterChips;
}

interface PrimaryTextProps {
    customer: AssistantCustomer;
    searchTerm: string;
}

const PrimaryText = ({ customer, searchTerm }: PrimaryTextProps) => {
    const name = highlight(getFullName(customer), searchTerm);
    const { isVip } = customer;
    const vipText = useLocalizeText()({ textKey: TextKey.VIP });
    return (
        <Stack vertical={false} spacing="50">
            <span>{name}</span>
            {isVip && <StyledBadge content={vipText} variant="subtle" />}
        </Stack>
    );
};

interface SecondaryTextProps {
    customer: AssistantCustomer;
    searchTerm: string;
}

const SecondaryText = ({ customer, searchTerm }: SecondaryTextProps) => {
    const { email, reservationCount, telephone } = customer;

    const reservationLabel: string = useLocalizeText()({ textKey: TextKey.Reservation });
    let reservationCountText;
    if (reservationCount) {
        reservationCountText = `${UnicodeEntity.Space}${UnicodeEntity.PlusSign}${reservationCount.toString()}`;
    }

    const customerEmail: DescriptionItem = { value: highlight(email ?? '', searchTerm) };
    const customerTelephone: DescriptionItem = { value: highlight(telephone ?? '', searchTerm) };
    const customerReservationCount: DescriptionItem = {
        value: highlight(reservationCountText ?? '', searchTerm),
        prefix: reservationLabel,
    };

    const descriptions: DescriptionItem[] = [
        ...(email ? [customerEmail] : []),
        ...(telephone ? [customerTelephone] : []),
        ...(reservationCount ? [customerReservationCount] : []),
    ];

    return <SearchItemDescription descriptions={descriptions} />;
};

interface RightItemProps {
    customer: AssistantCustomer;
}

const RightItem = ({ customer }: RightItemProps) => (
    <Stack spacing="200" itemAlignment="center" alignment="end">
        {customer.isInHouse && (
            <Tag variant="info">
                <LocalizedText textKey={TextKey.InHouse} />
            </Tag>
        )}
    </Stack>
);

export const useCustomerList = ({ customers, searchTerm, selectedFilter }: CustomerListProps) => {
    const isCustomerManager = useCommanderSelector<boolean>(isCurrentProfileEnabledToManageCustomers);
    const chainId = useCommanderSelector<string>(getCurrentEnterpriseChainId);
    const plusIcon = <Icon size="small" value={IconName.PlusSimple} />;
    const resolveRoute = useResolveRoute();

    const list: Array<AssistantSearchListItemProps> = [];

    customers.forEach((customer) => {
        const url = resolveRoute(RouteName.CustomerDetail, { id: customer.id });
        const rightItem = <RightItem customer={customer} />;
        list.push({
            itemId: customer.id,
            primaryText: <PrimaryText customer={customer} searchTerm={searchTerm} />,
            secondaryText: <SecondaryText customer={customer} searchTerm={searchTerm} />,
            rightItem,
            leftIcon: <StyledLeftIcon size="small" value={IconName.Profile} />,
            routeAction: navigateInBaseModal(url),
            searchType: SearchType.Customer,
        });
    });

    if (isCustomerManager && customers.length > 0 && selectedFilter === SearchType.Customer) {
        list.push({
            itemId: AssistantSearchListItemButton.NewCustomer,
            primaryText: <LocalizedText textKey={TextKey.CustomerNew} />,
            leftIcon: plusIcon,
            routeAction: showRouteInModal(RouteName.CustomerCreate, {
                query: {
                    RedirectToProfile: true,
                    ChainId: chainId,
                    LastName: searchTerm,
                },
            }),
        });
    }
    return list;
};
